import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { HeroImageEffect, UIBackgroundStyle } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import tw from "twin.macro";
import {
    defaultWireAppAccentContextOverlay,
    defaultWireAppAccentContextOverlayUndo,
    defaultWireAppImageBrightenContextOverlay,
    defaultWireAppImageDarkenContextOverlay,
} from "webapp/lib/theme/core-themes/wireapp-theme";
import { styled } from "webapp/styled-components";
import Img from "webapp/ui/smart/img/img";

interface Props {
    background?: UIBackgroundStyle;
    backgroundImage?: string;
    backgroundEffect?: HeroImageEffect;
    blur?: boolean;
    className?: string;
    id?: string | undefined;
    ignoreHighlight?: boolean;
}

const WireContainerStyle = styled.div`
    &.style-dark,
    &.style-image.darken {
        background-color: ${p => p.theme.n700};
    }

    &.style-image {
        background-color: ${p => p.theme.n200};
    }

    &.style-highlight,
    &.style-image.lighten {
        background-color: ${p => p.theme.n0};
    }

    &.darken .background-image,
    &.lighten .background-image {
        opacity: 0.5;
    }

    &.blur {
        overflow: hidden;
    }

    &:first-child.full-bleed,
    .full-bleed + &.full-bleed {
        margin-top: 0;
    }

    &.full-bleed:last-child {
        padding-bottom: 0;
    }

    &.reactive {
        opacity: var(--component-highlight-opacity);
    }
`;

export const WireContainer: GlideFC<React.PropsWithChildren<Props>> = p => {
    const {
        children,
        background = UIBackgroundStyle.None,
        backgroundImage,
        backgroundEffect = HeroImageEffect.None,
        blur = false,
        className,
        ignoreHighlight,
        id,
    } = p;

    let theme = useWireAppTheme();

    let content = children;
    if (background === UIBackgroundStyle.Card) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlayUndo]);
        content = (
            <div
                tw="bg-bg-front shadow-md rounded-lg px-6
                    gp-xl:px-8">
                <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>
            </div>
        );
    } else if (background === UIBackgroundStyle.Image && backgroundEffect === HeroImageEffect.Lighten) {
        theme = mergeTheme(theme, [defaultWireAppImageBrightenContextOverlay]);
        content = <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Image) {
        theme = mergeTheme(theme, [defaultWireAppImageDarkenContextOverlay]);
        content = <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Accent || background === UIBackgroundStyle.Dark) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlay]);
        content = <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Highlight) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlayUndo]);
        content = <TailwindThemeProvider theme={theme}>{content}</TailwindThemeProvider>;
    }

    return (
        <SectionStyleProvider value={background}>
            <WireContainerStyle
                id={id}
                className={classNames(
                    "section-wrap",
                    "component-root",
                    ignoreHighlight !== true && "reactive",
                    background !== UIBackgroundStyle.None && background !== UIBackgroundStyle.Card && "full-bleed",
                    background === UIBackgroundStyle.Accent && "accent-bg",
                    background === UIBackgroundStyle.Image && blur && "blur",
                    backgroundEffect.toLocaleLowerCase(),
                    `style-${background.toLowerCase()}`,
                    className
                )}
                tw="isolate relative w-full flex justify-center mt-6 last:pb-12 transition-opacity">
                {background === UIBackgroundStyle.Image && backgroundImage !== undefined && (
                    <Img
                        className="background-image"
                        css={[
                            tw`absolute inset-0 object-cover w-full h-full pointer-events-none filter`,
                            blur && tw`transform scale-125 blur-md`,
                        ]}
                        data-test="hero-background-image"
                        src={massageImageUrl(
                            backgroundImage,
                            {
                                width: window.innerWidth,
                                thumbnail: false,
                            },
                            undefined
                        )}
                        width={"100%"}
                        height={400}
                        draggable={false}
                    />
                )}
                <div
                    className="section-container"
                    // These classes are manually mirrored in wire-custom to
                    // allow embedded custom components to control their own
                    // container (e.g. draw shadows) but also match Glide's
                    // container layouts. Please update those as well.
                    tw="z-10 w-full relative flex flex-col px-5
                        gp-sm:(px-6)
                        gp-md:(px-7)
                        gp-lg:(px-8)
                        gp-2xl:(max-width[1260px])">
                    {content}
                </div>
            </WireContainerStyle>
        </SectionStyleProvider>
    );
};

export const SectionStyleContext = React.createContext<UIBackgroundStyle>(UIBackgroundStyle.None);

export function useSectionStyle() {
    return React.useContext(SectionStyleContext);
}

export const SectionStyleProvider: React.FC<{ value: UIBackgroundStyle }> = p => {
    let { value } = p;
    const parent = useSectionStyle();
    if (value === UIBackgroundStyle.None) {
        value = parent;
    }
    return <SectionStyleContext.Provider value={value}>{p.children}</SectionStyleContext.Provider>;
};
